/**
 * Phase 10 R1 — Labor Rate Resolver
 *
 * ⚠️  CONCEPT BOUNDARY (do not collapse):
 *
 *   Labor sell rate ≠ tech pay rate ≠ labor cost.
 *
 * This module produces only the **applied labor sell rate** — the dollars-per-hour
 * a shop charges a customer for a given job category. Sold hours, optiTime,
 * reference time, and tech pay time are **separate concepts** owned by later rounds:
 *
 *   • R2 — Ticket Labor Dollars (sold hours × applied rate)
 *   • R3 — Technician Pay Engine (tech pay time × tech pay rate)
 *   • R4 — GP Engine (labor sell − labor cost)
 *
 * Do NOT consume this module to compute pay or cost. It is a **sell-rate** function only.
 *
 * Resolution rule (first-pass, deterministic):
 *   • Diagnostic group  → diagnostic_labor_rate ?? default_labor_rate
 *   • Maintenance group → maintenance_labor_rate ?? default_labor_rate
 *   • Anything else     → default_labor_rate
 *
 * The diagnostic / maintenance group memberships below are intentionally inlined
 * (not derived from canonicalCategories.json) so the resolution boundary is
 * reviewable at a glance. They are a strict subset of canonical categories.
 */

export type LaborRateSource = 'diagnostic' | 'maintenance' | 'default';

export interface ShopLaborRates {
  default_labor_rate: number;
  diagnostic_labor_rate?: number | null;
  maintenance_labor_rate?: number | null;
}

export interface ResolvedLaborRate {
  rate: number;
  source: LaborRateSource;
  /** Human-readable explanation, surfaced in R2+ UI explainability panels. */
  reason: string;
}

/** Categories that resolve to the diagnostic labor rate. */
const DIAGNOSTIC_CATEGORIES = new Set<string>([
  'Diagnostics',
  'ADAS',
  'Electrical',
]);

/** Categories that resolve to the maintenance labor rate. */
const MAINTENANCE_CATEGORIES = new Set<string>([
  'Maintenance',
  'Inspections',
  'Tires',
]);

/**
 * Resolve the applied labor sell rate for a given job category.
 * Pure deterministic function — no I/O, safe to call from anywhere.
 */
export function resolveLaborRate(
  shopRates: ShopLaborRates,
  jobCategory: string | null | undefined,
): ResolvedLaborRate {
  const fallback = shopRates.default_labor_rate;
  const cat = (jobCategory || '').trim();

  if (DIAGNOSTIC_CATEGORIES.has(cat)) {
    const rate = shopRates.diagnostic_labor_rate;
    if (rate != null && rate > 0) {
      return {
        rate,
        source: 'diagnostic',
        reason: `Category '${cat}' → diagnostic_labor_rate`,
      };
    }
    return {
      rate: fallback,
      source: 'default',
      reason: `Category '${cat}' is diagnostic but diagnostic_labor_rate not set → default_labor_rate`,
    };
  }

  if (MAINTENANCE_CATEGORIES.has(cat)) {
    const rate = shopRates.maintenance_labor_rate;
    if (rate != null && rate > 0) {
      return {
        rate,
        source: 'maintenance',
        reason: `Category '${cat}' → maintenance_labor_rate`,
      };
    }
    return {
      rate: fallback,
      source: 'default',
      reason: `Category '${cat}' is maintenance but maintenance_labor_rate not set → default_labor_rate`,
    };
  }

  return {
    rate: fallback,
    source: 'default',
    reason: cat
      ? `Category '${cat}' has no specialty rate → default_labor_rate`
      : `No category provided → default_labor_rate`,
  };
}
